// Screen 2 - intent step (slice-2-onboarding.md §5; D33). Exactly one of six options OR
// free text (>=10 chars) is required. On submit this creates the App record (type +
// origin from wizard draft + intent + intentFreeText) - see src/state/wizard.tsx for the
// cited, resolved ordering conflict that puts app-creation here rather than at screen 1.
// The app-limit refusal (§2.4) surfaces here, at the real server-side default-deny point.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { isErr } from "@nightcrew/shared";
import type { AppIntent } from "@nightcrew/shared";
import { FocusedFlowShell } from "../../components/FocusedFlowShell.js";
import { OuterStepper } from "../../components/Stepper.js";
import { SubStepper } from "../../components/Stepper.js";
import { DeniedCard } from "../../components/DeniedCard.js";
import { RefusalCard } from "../../components/RefusalCard.js";
import { createApp } from "../../api/apps.js";
import type { AppLimitRefusal } from "../../api/errors.js";
import { isOwnerOrAdmin, useSession } from "../../state/session.js";
import { useWizard } from "../../state/wizard.js";

const INTENT_OPTIONS: readonly { readonly value: AppIntent; readonly label: string }[] = [
  { value: "keep_alive", label: "Keep it alive and maintained" },
  { value: "get_live", label: "Get it live, or launch it" },
  { value: "harden", label: "Harden it for real users" },
  { value: "revamp", label: "Revamp or improve it" },
  { value: "migrate", label: "Migrate it - a new stack or cloud" },
  { value: "deploy", label: "Just deploy it as-is" },
];

export const IntentStep = (): React.JSX.Element => {
  const { state: session } = useSession();
  const { draft, setAppId } = useWizard();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [choice, setChoice] = useState<AppIntent | null>(null);
  const [freeText, setFreeText] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<AppLimitRefusal | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [upgradeRequested, setUpgradeRequested] = useState(false);

  if (!isOwnerOrAdmin(session)) {
    return (
      <FocusedFlowShell title="What do you want done?">
        <DeniedCard message="Ask an owner or admin on this account, or ask them to make you an admin." />
      </FocusedFlowShell>
    );
  }

  if (draft.type === null || draft.origin === null) {
    return (
      <FocusedFlowShell
        title="What do you want done?"
        onBack={() => {
          void navigate("/apps/new");
        }}
      >
        <div className="card state-card">
          <p>Pick what kind of app this is first.</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              void navigate("/apps/new");
            }}
          >
            Back to type
          </button>
        </div>
      </FocusedFlowShell>
    );
  }

  if (refusal !== null) {
    return (
      <FocusedFlowShell
        title="What do you want done?"
        onBack={() => {
          setRefusal(null);
        }}
      >
        {upgradeRequested ? (
          <div className="card pad" role="status">
            <p>Noted. We will follow up about upgrading - no payment was collected.</p>
          </div>
        ) : (
          <RefusalCard
            refusal={refusal}
            // No endpoint is listed for this intent-capture call yet (Slice 7 wires the
            // real checkout, ADR-0016 §8) - recorded locally for now, never a dead end.
            onAskAboutUpgrading={() => {
              setUpgradeRequested(true);
            }}
            onFreeUpASlot={() => {
              void navigate("/apps");
            }}
            onBackToApps={() => {
              void navigate("/apps");
            }}
          />
        )}
      </FocusedFlowShell>
    );
  }

  const freeTextValid = freeText.trim().length >= 10;

  const nameValid = name.trim().length >= 1;

  const handleSubmit = async (): Promise<void> => {
    const { type, origin } = draft;
    if (type === null || origin === null) return;
    if (!nameValid) {
      setValidationError("Give this app a name so you can find it in your portfolio.");
      return;
    }
    if (choice === null && !freeTextValid) {
      setValidationError("Pick one, or tell us in your own words below.");
      return;
    }
    setValidationError(null);
    setSubmitError(null);
    setSubmitting(true);
    const result = await createApp({
      name: name.trim(),
      type,
      origin,
      intent: choice ?? "deploy",
      intentFreeText: freeTextValid ? freeText.trim() : undefined,
    });
    setSubmitting(false);
    if (isErr(result)) {
      if (result.error.kind === "app_limit_refusal") {
        setRefusal(result.error.refusal);
        return;
      }
      setSubmitError("That did not save. Try again - nothing you typed was lost.");
      return;
    }
    setAppId(result.value.id);
    void navigate("/apps/new/connect");
  };

  return (
    <FocusedFlowShell
      title="What do you want done?"
      onBack={() => {
        void navigate("/apps/new");
      }}
      cta={
        <button
          type="button"
          className="btn btn-primary"
          disabled={submitting}
          onClick={() => {
            void handleSubmit();
          }}
        >
          {submitting ? "Saving..." : "Continue"}
        </button>
      }
    >
      <OuterStepper active="connect" />
      <SubStepper states={{ repo: "pending", intent: "pending", cloud: "pending" }} />
      {validationError !== null ? (
        <p className="field-error" role="alert" aria-live="polite">
          {validationError}
        </p>
      ) : null}
      {submitError !== null ? (
        <p className="field-error" role="alert">
          {submitError}
        </p>
      ) : null}
      <div className="mb16">
        <label className="field-label" htmlFor="app-name">
          App name
        </label>
        <input
          id="app-name"
          className="field"
          type="text"
          maxLength={120}
          placeholder="e.g. Nesti"
          value={name}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
      </div>
      <div className="radiogroup" role="radiogroup" aria-label="What do you want done?">
        {INTENT_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={choice === option.value}
            className="radio-option"
            onClick={() => {
              setChoice(option.value);
            }}
          >
            <span className="radio-dot" aria-hidden="true" />
            {option.label}
          </button>
        ))}
      </div>
      <div className="mt16">
        <label className="field-label" htmlFor="intent-free-text">
          Something else - tell us in your own words
        </label>
        <textarea
          id="intent-free-text"
          className="field"
          rows={3}
          placeholder='e.g. "I need this stable enough to hand to a co-founder."'
          value={freeText}
          onChange={(event) => {
            setFreeText(event.target.value);
            setChoice(null);
          }}
        />
        <div className="row aic gap8 mt8">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled
            aria-describedby="voice-hint"
          >
            Voice
          </button>
          <span id="voice-hint" className="muted" style={{ fontSize: 12 }}>
            Voice notes are coming soon. Type it for now.
          </span>
        </div>
      </div>
    </FocusedFlowShell>
  );
};
